import { useSyncDarkMode } from "@/app/darkMode.ts";
import { SidebarLayout } from "@/catalyst/sidebar-layout.tsx";
import {
    Sidebar,
    SidebarBody,
    SidebarFooter,
    SidebarHeader,
    SidebarItem,
    SidebarLabel,
    SidebarSection,
    SidebarSpacer,
} from "@/catalyst/sidebar.tsx";
import WorkspacePicker from "@/common/WorkspacePicker";
import {
    Cog6ToothIcon,
    QuestionMarkCircleIcon,
    Squares2X2Icon,
} from "@heroicons/react/20/solid";
import { BuildingOffice2Icon, ChartBarIcon } from "@heroicons/react/24/solid";
import {
    ArrowRightStartOnRectangleIcon,
    ChevronUpIcon,
    MoonIcon,
    UserIcon,
} from "@heroicons/react/16/solid";
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from "@/catalyst/dropdown.tsx";
import { Avatar } from "@/catalyst/avatar.tsx";
import DarkModeToggle from "@/common/DarkModeToggle/DarkModeToggle.tsx";
import { useContext } from "react";
import UserContext from "@/app/UserContext.ts";
import { assertNotUndefined } from "@/util/typeguards.ts";
import { useSignOut } from "@/hooks/use-signout.ts";
import PlanPicker from "../PlanPicker";
import { FullHeightMainContainer } from "@/common/Container";
import Link from "@/common/Link";
import { Logo } from "@/common/Logo/Logo.tsx";

export default function DisabledUiWithPricingTable() {
    // This screen mounts instead of App, so it has to keep the document's
    // dark class in step itself or the toggle in the footer does nothing.
    const darkMode = useSyncDarkMode();
    const user = useContext(UserContext);
    assertNotUndefined(user);
    const { signOut } = useSignOut();
    return (
        <SidebarLayout
            navbar={<></>}
            sidebar={() => (
                <Sidebar>
                    <SidebarHeader style={{ filter: "blur(3px)" }}>
                        <WorkspacePicker setNewWorkspaceDialogOpen={() => {}} />
                        {/*<SidebarSection className="max-lg:hidden">*/}
                        {/*    <SidebarItem href="/search">*/}
                        {/*        <MagnifyingGlassIcon />*/}
                        {/*        <SidebarLabel>Search</SidebarLabel>*/}
                        {/*    </SidebarItem>*/}
                        {/*    <SidebarItem href="/inbox">*/}
                        {/*        <InboxIcon />*/}
                        {/*        <SidebarLabel>Inbox</SidebarLabel>*/}
                        {/*    </SidebarItem>*/}
                        {/*</SidebarSection>*/}
                    </SidebarHeader>
                    <SidebarBody style={{ filter: "blur(3px)" }}>
                        <SidebarSection>
                            <SidebarItem
                                className="relative"
                                current={location.pathname === "/"}
                            >
                                <Squares2X2Icon />
                                <SidebarLabel>Dashboard</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem
                                current={location.pathname === "/reporting"}
                            >
                                <ChartBarIcon />
                                <SidebarLabel>Reporting</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem
                                current={location.pathname === "/contacts"}
                            >
                                <UserIcon />
                                <SidebarLabel>Contacts</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem
                                current={location.pathname === "/settings"}
                            >
                                <Cog6ToothIcon />
                                <SidebarLabel>Settings</SidebarLabel>
                            </SidebarItem>
                        </SidebarSection>
                        {/*<SidebarSection className="max-lg:hidden">*/}
                        {/*    <SidebarHeading>Upcoming Events</SidebarHeading>*/}
                        {/*    <SidebarItem href="/events/1">*/}
                        {/*        Bear Hug: Live in Concert*/}
                        {/*    </SidebarItem>*/}
                        {/*    <SidebarItem href="/events/2">*/}
                        {/*        Viking People*/}
                        {/*    </SidebarItem>*/}
                        {/*    <SidebarItem href="/events/3">*/}
                        {/*        Six Fingers — DJ Set*/}
                        {/*    </SidebarItem>*/}
                        {/*    <SidebarItem href="/events/4">*/}
                        {/*        We All Look The Same*/}
                        {/*    </SidebarItem>*/}
                        {/*    <SidebarItem href="/events/4">*/}
                        {/*        */}
                        {/*    </SidebarItem>*/}
                        {/*</SidebarSection>*/}
                        <SidebarSpacer />
                        <SidebarSection>
                            <SidebarItem href="mailto:support@spectaclehq.com">
                                <QuestionMarkCircleIcon />
                                <SidebarLabel>Support</SidebarLabel>
                            </SidebarItem>
                        </SidebarSection>
                    </SidebarBody>
                    <SidebarFooter className="max-lg:hidden">
                        <Dropdown>
                            <DropdownButton as={SidebarItem}>
                                <span className="flex min-w-0 items-center gap-3">
                                    <Avatar
                                        generateBgColor={true}
                                        src={user.picture}
                                        className="size-6"
                                        name={user.name}
                                        square
                                        alt=""
                                    />
                                    <span className="min-w-0">
                                        <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">
                                            {user.name}
                                        </span>
                                    </span>
                                </span>
                                <ChevronUpIcon />
                            </DropdownButton>
                            <DropdownMenu
                                className="min-w-64"
                                anchor="top start"
                            >
                                <DropdownItem>
                                    <UserIcon />
                                    <DropdownLabel>
                                        Account settings
                                    </DropdownLabel>
                                </DropdownItem>
                                <DropdownItem>
                                    <BuildingOffice2Icon />
                                    <DropdownLabel>
                                        Organization settings
                                    </DropdownLabel>
                                </DropdownItem>
                                <DropdownDivider />
                                <DropdownItem>
                                    <MoonIcon />
                                    <DarkModeToggle />
                                </DropdownItem>
                                <DropdownDivider />
                                <DropdownItem onClick={signOut}>
                                    <ArrowRightStartOnRectangleIcon />
                                    <DropdownLabel>Sign out</DropdownLabel>
                                </DropdownItem>
                            </DropdownMenu>
                        </Dropdown>
                    </SidebarFooter>
                </Sidebar>
            )}
        >
            <FullHeightMainContainer>
                <div className="flex h-full w-full grow flex-col items-center justify-center overflow-hidden">
                    <div className="flex w-full flex-col items-center justify-center gap-16 text-default">
                        <div className="max-w-md text-center text-xl">
                            <Logo
                                className="mx-auto mb-8 w-80"
                                variant={
                                    darkMode ? "color+white" : "color+primary"
                                }
                            />
                            Your subscription has{" "}
                            <span className="font-semibold">expired</span>.
                            Please choose a new plan. For full plan details,
                            please check{" "}
                            <Link
                                to={`https://${import.meta.env.VITE_DOMAIN}/pricing`}
                                external={true}
                            >
                                our pricing
                            </Link>
                        </div>
                        <div className="w-full">
                            <PlanPicker
                                lockedCurrency={
                                    user.currentOrganization
                                        .lockedBillingCurrency
                                }
                            />
                        </div>
                    </div>
                </div>
            </FullHeightMainContainer>
        </SidebarLayout>
    );
}
